import { useParams, Link, useNavigate } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import { motion } from 'framer-motion'

const ServiceDetail = () => {
  const { id } = useParams()
  const { language } = useLanguage()
  const navigate = useNavigate()

  const services = {
    'same-day-delivery': {
      icon: '🚀',
      title: language === 'en' ? 'Same-Day Delivery' : 'توصيل في نفس اليوم',
      description: language === 'en'
        ? 'Urgent deliveries completed within hours. Perfect for time-sensitive documents, food orders, and emergency shipments.'
        : 'التوصيلات العاجلة مكتملة في غضون ساعات. مثالي للمستندات الحساسة للوقت وطلبات الطعام والشحنات الطارئة.',
      details: language === 'en'
        ? 'Our same-day delivery service ensures your urgent packages reach their destination within hours. Perfect for time-sensitive documents, food orders, medical supplies, and emergency shipments. We prioritize these deliveries and provide real-time tracking updates so you can monitor your package every step of the way.'
        : 'تضمن خدمة التوصيل في نفس اليوم وصول طرودك العاجلة إلى وجهتها في غضون ساعات. مثالي للمستندات الحساسة للوقت وطلبات الطعام والإمدادات الطبية والشحنات الطارئة. نعطي أولوية لهذه التوصيلات ونوفر تحديثات تتبع في الوقت الفعلي حتى تتمكن من مراقبة طردك في كل خطوة.',
      features: language === 'en'
        ? [
            'Express service within 2-4 hours',
            'Real-time GPS tracking',
            'Priority handling',
            'SMS and email notifications',
            'Photo proof of delivery',
          ]
        : [
            'خدمة سريعة خلال 2-4 ساعات',
            'تتبع GPS في الوقت الفعلي',
            'معالجة أولوية',
            'إشعارات SMS والبريد الإلكتروني',
            'إثبات الصورة للتوصيل',
          ],
      pricing: language === 'en'
        ? 'Starting from AED 25 for same-day delivery within the same city. Express service (2-4 hours) available at AED 35.'
        : 'بدءًا من 25 درهم إماراتي للتوصيل في نفس اليوم داخل نفس المدينة. خدمة سريعة (2-4 ساعات) متاحة بسعر 35 درهم إماراتي.',
    },
    'scheduled-delivery': {
      icon: '📅',
      title: language === 'en' ? 'Scheduled Delivery' : 'توصيل مجدول',
      description: language === 'en'
        ? 'Plan your deliveries in advance. Set recurring routes for regular shipments and optimize your logistics.'
        : 'خطط لتوصيلاتك مسبقًا. حدد مسارات متكررة للشحنات المنتظمة وحدّث لوجستياتك.',
      details: language === 'en'
        ? 'Schedule your deliveries in advance to optimize your logistics and reduce costs. Set up recurring routes for regular shipments, choose specific time slots, and manage all your scheduled deliveries from one dashboard. Perfect for businesses with regular delivery needs.'
        : 'قم بجدولة توصيلاتك مسبقًا لتحسين لوجستياتك وتقليل التكاليف. قم بإعداد مسارات متكررة للشحنات المنتظمة واختر فترات زمنية محددة وأدر جميع التوصيلات المجدولة من لوحة تحكم واحدة. مثالي للشركات التي لديها احتياجات توصيل منتظمة.',
      features: language === 'en'
        ? [
            'Flexible scheduling options',
            'Recurring route setup',
            'Cost optimization',
            'Bulk delivery discounts',
            'Dashboard management',
          ]
        : [
            'خيارات جدولة مرنة',
            'إعداد مسار متكرر',
            'تحسين التكلفة',
            'خصومات التوصيل بالجملة',
            'إدارة لوحة التحكم',
          ],
      pricing: language === 'en'
        ? 'Standard scheduled delivery: AED 15-20. Recurring routes: Up to 30% discount on bulk orders.'
        : 'التوصيل المجدول القياسي: 15-20 درهم إماراتي. المسارات المتكررة: خصم يصل إلى 30% على الطلبات بالجملة.',
    },
    'ecommerce-fulfillment': {
      icon: '📦',
      title: language === 'en' ? 'E-Commerce Fulfillment' : 'تنفيذ التجارة الإلكترونية',
      description: language === 'en'
        ? 'End-to-end fulfillment for online stores. Integration with major e-commerce platforms and custom solutions.'
        : 'تنفيذ شامل للمتاجر عبر الإنترنت. التكامل مع منصات التجارة الإلكترونية الرئيسية والحلول المخصصة.',
      details: language === 'en'
        ? 'Complete e-commerce fulfillment solution with seamless integration to major platforms like Shopify, WooCommerce, and Magento. We handle order processing, inventory management, packaging, and delivery. Our API allows real-time inventory sync and automated order processing.'
        : 'حل تنفيذ شامل للتجارة الإلكترونية مع تكامل سلس مع المنصات الرئيسية مثل Shopify وWooCommerce وMagento. نتعامل مع معالجة الطلبات وإدارة المخزون والتغليف والتوصيل. يسمح API الخاص بنا بمزامنة المخزون في الوقت الفعلي ومعالجة الطلبات الآلية.',
      features: language === 'en'
        ? [
            'Platform integration (Shopify, WooCommerce, Magento)',
            'Automated order processing',
            'Real-time inventory sync',
            'Custom packaging options',
            'API access',
          ]
        : [
            'تكامل المنصة (Shopify, WooCommerce, Magento)',
            'معالجة الطلبات الآلية',
            'مزامنة المخزون في الوقت الفعلي',
            'خيارات التغليف المخصصة',
            'الوصول إلى API',
          ],
      pricing: language === 'en'
        ? 'Custom pricing based on volume. Starting from AED 12 per order for high-volume clients. Contact us for a quote.'
        : 'تسعير مخصص بناءً على الحجم. بدءًا من 12 درهم إماراتي لكل طلب للعملاء ذوي الحجم الكبير. اتصل بنا للحصول على عرض أسعار.',
    },
    'document-delivery': {
      icon: '📄',
      title: language === 'en' ? 'Document Delivery' : 'توصيل المستندات',
      description: language === 'en'
        ? 'Fast and secure document delivery. Confidential handling for legal, financial, and corporate documents.'
        : 'توصيل سريع وآمن للمستندات. معالجة سرية للمستندات القانونية والمالية والشركات.',
      details: language === 'en'
        ? 'Specialized secure document delivery service for legal, financial, and corporate documents. All documents are handled with strict confidentiality protocols, sealed packaging, and chain of custody tracking. We provide detailed proof of delivery including recipient signatures and timestamps.'
        : 'خدمة توصيل مستندات آمنة متخصصة للمستندات القانونية والمالية والشركات. يتم التعامل مع جميع المستندات ببروتوكولات سرية صارمة وتغليف مختوم وتتبع سلسلة الحراسة. نقدم دليلاً مفصلاً على التسليم بما في ذلك توقيعات المستلم والطوابع الزمنية.',
      features: language === 'en'
        ? [
            'Confidential handling',
            'Sealed packaging',
            'Chain of custody tracking',
            'Signature verification',
            'Legal document support',
          ]
        : [
            'معالجة سرية',
            'تغليف مختوم',
            'تتبع سلسلة الحراسة',
            'التحقق من التوقيع',
            'دعم المستندات القانونية',
          ],
      pricing: language === 'en'
        ? 'Standard document delivery: AED 20. Express service: AED 30. Bulk discounts available.'
        : 'توصيل المستندات القياسي: 20 درهم إماراتي. الخدمة السريعة: 30 درهم إماراتي. خصومات بالجملة متاحة.',
    },
    'bookings-returns': {
      icon: '📝',
      title: language === 'en' ? 'Easy Bookings & Returns' : 'حجوزات سهلة وإرجاع',
      description: language === 'en'
        ? 'Simple and intuitive booking system for all your delivery needs. Hassle-free returns process with quick pickup and processing.'
        : 'نظام حجز بسيط وبديهي لجميع احتياجات التوصيل الخاصة بك. عملية إرجاع خالية من المتاعب مع استلام ومعالجة سريعة.',
      details: language === 'en'
        ? 'Our user-friendly booking system makes it easy to schedule pickups and deliveries. Book online, via phone, or through our mobile app. Returns are processed quickly with same-day pickup available. Track your return status in real-time and receive notifications at every step.'
        : 'يجعل نظام الحجز سهل الاستخدام لدينا من السهل جدولة عمليات الاستلام والتوصيل. احجز عبر الإنترنت أو عبر الهاتف أو من خلال تطبيقنا المحمول. يتم معالجة الإرجاعات بسرعة مع توفر الاستلام في نفس اليوم. تتبع حالة الإرجاع الخاصة بك في الوقت الفعلي وتلقي إشعارات في كل خطوة.',
      features: language === 'en'
        ? [
            'Online booking portal',
            'Mobile app booking',
            'Phone booking support',
            'Same-day return pickup',
            'Return status tracking',
          ]
        : [
            'بوابة الحجز عبر الإنترنت',
            'حجز التطبيق المحمول',
            'دعم الحجز عبر الهاتف',
            'استلام الإرجاع في نفس اليوم',
            'تتبع حالة الإرجاع',
          ],
      pricing: language === 'en'
        ? 'Booking is free. Return pickup: AED 15. Processing fee: AED 5 per return.'
        : 'الحجز مجاني. استلام الإرجاع: 15 درهم إماراتي. رسوم المعالجة: 5 دراهم إماراتية لكل إرجاع.',
    },
    'order-tracking': {
      icon: '🔍',
      title: language === 'en' ? 'Order Tracking' : 'تتبع الطلبات',
      description: language === 'en'
        ? 'Real-time order tracking from pickup to delivery. Get instant updates and notifications at every stage of your shipment journey.'
        : 'تتبع الطلبات في الوقت الفعلي من الاستلام إلى التسليم. احصل على تحديثات وإشعارات فورية في كل مرحلة من رحلة شحنتك.',
      details: language === 'en'
        ? 'Track your orders in real-time with our advanced GPS tracking system. Receive instant SMS and email notifications at every milestone: pickup confirmation, in-transit updates, out for delivery, and delivery confirmation. Access tracking information through our website, mobile app, or WhatsApp.'
        : 'تتبع طلباتك في الوقت الفعلي مع نظام تتبع GPS المتقدم لدينا. احصل على إشعارات SMS والبريد الإلكتروني الفورية في كل معلم: تأكيد الاستلام وتحديثات العبور والتوصيل وتأكيد التسليم. الوصول إلى معلومات التتبع من خلال موقعنا الإلكتروني أو التطبيق المحمول أو واتساب.',
      features: language === 'en'
        ? [
            'Real-time GPS tracking',
            'SMS notifications',
            'Email updates',
            'WhatsApp integration',
            'Delivery timeline',
          ]
        : [
            'تتبع GPS في الوقت الفعلي',
            'إشعارات SMS',
            'تحديثات البريد الإلكتروني',
            'تكامل واتساب',
            'الجدول الزمني للتوصيل',
          ],
      pricing: language === 'en'
        ? 'Tracking is included free with all delivery services.'
        : 'التتبع مجاني مع جميع خدمات التوصيل.',
    },
    'secure-delivery': {
      icon: '🛡️',
      title: language === 'en' ? 'Secure Delivery' : 'توصيل آمن',
      description: language === 'en'
        ? 'Your packages are handled with the utmost care and security. Professional handling, insurance options, and secure delivery protocols.'
        : 'يتم التعامل مع طرودك بأقصى درجات العناية والأمان. معالجة احترافية وخيارات التأمين وبروتوكولات التوصيل الآمنة.',
      details: language === 'en'
        ? 'Every package is handled by trained professionals following strict security protocols. We offer insurance coverage up to AED 10,000 for valuable items. All deliveries include tamper-proof packaging, signature verification, and photo proof of delivery. Our secure handling ensures your items arrive safely and intact.'
        : 'يتم التعامل مع كل طرد من قبل محترفين مدربين يتبعون بروتوكولات أمنية صارمة. نقدم تغطية تأمينية تصل إلى 10,000 درهم إماراتي للعناصر القيمة. تشمل جميع التوصيلات تغليفًا مقاومًا للعبث والتحقق من التوقيع وإثبات الصورة للتوصيل. تضمن معالجتنا الآمنة وصول عناصرك بأمان وسليمة.',
      features: language === 'en'
        ? [
            'Insurance up to AED 10,000',
            'Tamper-proof packaging',
            'Signature verification',
            'Photo proof of delivery',
            'Professional handlers',
          ]
        : [
            'تأمين يصل إلى 10,000 درهم إماراتي',
            'تغليف مقاوم للعبث',
            'التحقق من التوقيع',
            'إثبات الصورة للتوصيل',
            'معالجون محترفون',
          ],
      pricing: language === 'en'
        ? 'Standard secure delivery: AED 20. Insurance: 2% of item value (minimum AED 10).'
        : 'التوصيل الآمن القياسي: 20 درهم إماراتي. التأمين: 2% من قيمة العنصر (الحد الأدنى 10 دراهم إماراتية).',
    },
    'cod-payment': {
      icon: '💳',
      title: language === 'en' ? 'Cash & Card on Delivery' : 'نقد وبطاقة عند التسليم',
      description: language === 'en'
        ? 'Flexible payment options for your customers. We handle payment collection securely and transfer funds promptly.'
        : 'خيارات دفع مرنة لعملائك. نحن نتعامل مع تحصيل الدفع بأمان ونحول الأموال على الفور.',
      details: language === 'en'
        ? 'Offer your customers the convenience of cash or card payment upon delivery. We accept all major credit and debit cards, and handle cash collection securely. Funds are transferred to your account within 24-48 hours. Our payment gateway is PCI-DSS compliant and provides instant payment confirmations.'
        : 'قدم لعملائك راحة الدفع نقدًا أو بالبطاقة عند التسليم. نقبل جميع بطاقات الائتمان والخصم الرئيسية ونتعامل مع تحصيل النقد بأمان. يتم تحويل الأموال إلى حسابك خلال 24-48 ساعة. بوابة الدفع لدينا متوافقة مع PCI-DSS وتوفر تأكيدات الدفع الفورية.',
      features: language === 'en'
        ? [
            'Cash on delivery',
            'Card payment support',
            'Secure collection',
            'Fast fund transfers (24-48h)',
            'PCI-DSS compliant',
          ]
        : [
            'الدفع نقدًا عند التسليم',
            'دعم الدفع بالبطاقة',
            'تحصيل آمن',
            'تحويلات أموال سريعة (24-48 ساعة)',
            'متوافق مع PCI-DSS',
          ],
      pricing: language === 'en'
        ? 'COD service fee: 2% of transaction value (minimum AED 5). Card processing: 2.5% fee.'
        : 'رسوم خدمة COD: 2% من قيمة المعاملة (الحد الأدنى 5 دراهم إماراتية). معالجة البطاقة: رسوم 2.5%.',
    },
  }

  const service = services[id]

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {language === 'en' ? 'Service Not Found' : 'الخدمة غير موجودة'}
          </h1>
          <Link to="/services" className="text-primary-600 hover:underline">
            {language === 'en' ? 'Back to Services' : 'العودة إلى الخدمات'}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-primary-500 py-12 md:py-20 overflow-hidden">
        {/* Grid Pattern Background */}
        <div className="absolute inset-0 z-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate(-1)}
            className="mb-8 text-white/90 hover:text-white flex items-center gap-2 transition-colors"
          >
            <span>←</span>
            <span>{language === 'en' ? 'Back to Services' : 'العودة إلى الخدمات'}</span>
          </button>
          
          <div className="text-center">
            <div className="text-7xl md:text-8xl mb-6">{service.icon}</div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {service.title}
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
              {service.description}
            </p>
          </div>
        </div>
      </section>

      {/* Details Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              {language === 'en' ? 'Service Details' : 'تفاصيل الخدمة'}
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              {service.details}
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {language === 'en' ? 'Key Features' : 'الميزات الرئيسية'}
            </h3>
            <ul className="space-y-3 mb-8">
              {service.features.map((feature, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start gap-3 text-gray-700"
                >
                  <span className="text-primary-600 text-xl mt-1">✓</span>
                  <span className="text-lg">{feature}</span>
                </motion.li>
              ))}
            </ul>

            <div className="bg-primary-50 rounded-xl p-6 border-l-4 border-primary-500">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {language === 'en' ? 'Pricing' : 'التسعير'}
              </h3>
              <p className="text-gray-700 text-lg">
                {service.pricing}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {language === 'en' ? 'Ready to Use This Service?' : 'هل أنت مستعد لاستخدام هذه الخدمة؟'}
          </h2>
          <p className="text-xl text-gray-700 mb-8">
            {language === 'en'
              ? 'Contact us today to get started with this service.'
              : 'اتصل بنا اليوم للبدء في استخدام هذه الخدمة.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="px-8 py-4 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors shadow-lg hover:shadow-xl"
            >
              {language === 'en' ? 'Contact Us' : 'اتصل بنا'}
            </Link>
            <Link
              to="/services"
              className="px-8 py-4 bg-white text-primary-600 border-2 border-primary-600 rounded-lg font-semibold hover:bg-primary-50 transition-colors"
            >
              {language === 'en' ? 'View All Services' : 'عرض جميع الخدمات'}
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

export default ServiceDetail
